import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import type { CalendarEventDto, CalendarOptions } from '@/types/calendar';
import type { ClientSleepSession } from './sleep.service';

// Календар клієнта для продакт-менеджера (/pm/client/:id/calendar), 2026-10-10. Окремі маршрути бекенду
// `pm/clients/:clientId/...` - лише перегляд; форма даних та сама, що в календарі експерта.

export interface PmClientEventsParams {
  calendarId?: string;
  eventTypeIds?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}

export const getPmClientCalendarOptions = async (clientId: number): Promise<CalendarOptions> => {
  const response = await api.get<ApiResponse<CalendarOptions>>(`/pm/clients/${clientId}/calendar/options`);
  return unwrapApiResponse<CalendarOptions>(response);
};

export const getPmClientCalendarEvents = async (clientId: number, params: PmClientEventsParams): Promise<CalendarEventDto[]> => {
  const response = await api.get<ApiResponse<CalendarEventDto[]>>(`/pm/clients/${clientId}/calendar/events`, { params });
  const data = unwrapApiResponse<CalendarEventDto[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getPmClientCalendarEvent = async (clientId: number, eventId: number): Promise<CalendarEventDto> => {
  const response = await api.get<ApiResponse<CalendarEventDto>>(`/pm/clients/${clientId}/calendar/events/${eventId}`);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const getPmClientSleepSessions = async (clientId: number, from: Date, to: Date): Promise<ClientSleepSession[]> => {
  const response = await api.get<ApiResponse<ClientSleepSession[]>>(`/pm/clients/${clientId}/sleep/sessions`, {
    params: { from: from.toISOString(), to: to.toISOString() },
  });
  const data = unwrapApiResponse<ClientSleepSession[]>(response);
  return Array.isArray(data) ? data : [];
};

// Що ввів клієнт для AI-подій (подія з batchId): текст і події, що з нього вийшли.
export interface PmClientAiBatch {
  batchId: string;
  eventType: 'food' | 'workout' | 'medication';
  rawInput: string | null;
  recognitionStatus: 'pending' | 'done' | 'failed' | null;
  contextDate: string | null;
  createdAt: string | null;
  events: Array<{ id: number; title: string; eventTypeCode: string; scheduledLdt: string | null }>;
}

export const getPmClientAiBatch = async (clientId: number, batchId: string): Promise<PmClientAiBatch> => {
  const response = await api.get<ApiResponse<PmClientAiBatch>>(`/pm/clients/${clientId}/calendar/batches/${batchId}`);
  return unwrapApiResponse<PmClientAiBatch>(response);
};


// Як подія впливає на енергетичний баланс клієнта (ті самі формули, що й денний баланс на бекенді).
export interface PmEventEnergy {
  direction: 'intake' | 'burn' | 'none';
  method: 'library_food' | 'library_workout_met' | 'ai_totals' | 'none';
  kcal: number | null;
  macros?: { protein: number; fat: number; carbs: number };
  inputs?: Record<string, any>;
  note?: string;
}

export const getPmClientEventEnergy = async (clientId: number, eventId: number): Promise<PmEventEnergy> => {
  const response = await api.get<ApiResponse<PmEventEnergy>>(`/pm/clients/${clientId}/calendar/events/${eventId}/energy`);
  return unwrapApiResponse<PmEventEnergy>(response);
};
